// ═══════════════════════════════════════════════════════════════════════════
// El cuadernillo — una página por vino, con sus tres fichas
//
// El operador: "necesito un cuadernillo en donde estén todos los productos
// seleccionados con su ficha técnica, la que mostraría a un restaurantero un
// vendedor para convencerlo de comprar el producto. Debe estar muy bien
// colocada la fotografía (si hay, si no un placeholder) adornos de marco, la
// descripción del vino, el maridaje, la historia detrás, la artista, autor de
// la obra, etc."
//
// ── EN QUÉ SE DISTINGUE DE LA LISTA DE PRECIOS ─────────────────────────────
//
// No la reemplaza: convive con ella. Son dos documentos con dos trabajos.
// `lista-precios.js` sirve para COTIZAR —precios con impuestos, descuento del
// cliente, existencias, todo apretado para que quepa— y este sirve para
// CONVENCER.
//
// Por eso aquí NO HAY UN SOLO PRECIO. Es una decisión del operador y no una
// omisión: una ficha que termina en una cifra invita a compararla con otra
// cifra, y D3 dice que en esta plataforma nunca se ordena por precio. El
// vendedor lleva los dos papeles y decide cuál pone sobre la mesa.
//
// Tampoco hay existencias. "Agotado" impreso en una ficha que se enseña para
// enamorar es la peor frase posible, y para cuando el cuadernillo llegue a la
// mesa el número ya cambió.
//
// ── LO QUE ESTÁ VACÍO NO SE IMPRIME ────────────────────────────────────────
//
// Ninguna sección se dibuja si su texto falta: ni su título, ni su regla, ni
// su hueco. Es la propiedad que hace que esto sirva para las demás vinícolas
// —que llenarán unos campos y otros no— y la que permite que la semblanza de
// una artista pueda estar pendiente sin que la página se vea rota.
//
// ── LAS FOTOGRAFÍAS ────────────────────────────────────────────────────────
//
// Las once que hay hoy miden 240×320 px. En la lista de precios van a 30 mm y
// se ven bien; una página de cuadernillo pedía más. La caja quedó en 64 mm,
// donde 240 px dan unos 95 dpi: se sostiene impreso, sin ser bueno. Con
// fotografías de 1000 px de lado este documento cambia de categoría, y no hay
// que tocar una línea de aquí para lograrlo.
// ═══════════════════════════════════════════════════════════════════════════

import { LOGO_BASE64 } from "./logo.js";
import { guardarPDF } from "./guardar.js";

function crearDocPredeterminado() {
  const { jsPDF } = window.jspdf;
  return new jsPDF({ unit: "mm", format: "letter" });
}

// ── Paleta ─────────────────────────────────────────────────────────────────
// La de la marca (CLAUDE.md §4), llevada a papel blanco: el coral manda, el
// oro hace los adornos y el texto es gris muy oscuro en vez de negro puro,
// que impreso se ve duro.
const CORAL = [240, 93, 73];
const ORO = [201, 169, 110];
const TINTA = [38, 36, 34];
const SUAVE = [110, 105, 100];
const CREMA = [248, 246, 240];
const FILETE = [222, 216, 204];

const ETIQUETAS_CATEGORIA = {
  tinto: "Vino tinto", blanco: "Vino blanco", naranja: "Vino naranja",
  vermu: "Vermú", cerveza: "Cerveza", destilado: "Destilado", otro: "",
};

/**
 * @param {{nombre?: string}} vinicola  De qué vinícola es el cuadernillo.
 *        Se recibe y no se toma de `REMITENTE` a propósito: REMITENTE es el
 *        emisor fiscal de UNA vinícola, hardcodeado desde el monolito, y este
 *        documento tiene que poder salir a nombre de cualquiera (D10).
 * @param {Array} productos  Los que el vendedor seleccionó, en ese orden.
 */
export function generarCuadernilloPDF(vinicola, productos, crearDoc = crearDocPredeterminado) {
  const doc = crearDoc();
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();

  const nombreVinicola = (vinicola && vinicola.nombre) || "";

  const ml = 21, mr = pw - 21;
  const anchoTotal = mr - ml;
  const yTecho = 27;              // primer renglón útil bajo el marco
  const ySuelo = ph - 24;         // último renglón útil sobre el pie

  // ── Utilería de dibujo ───────────────────────────────────────────────────

  const fuente = (familia, estilo, tam, color) => {
    doc.setFont(familia, estilo);
    doc.setFontSize(tam);
    doc.setTextColor(...color);
  };

  /** Rombo relleno: dos triángulos. El adorno que se repite en todo el documento. */
  const rombo = (cx, cy, r, color) => {
    doc.setFillColor(...color);
    doc.triangle(cx - r, cy, cx, cy - r, cx + r, cy, "F");
    doc.triangle(cx - r, cy, cx, cy + r, cx + r, cy, "F");
  };

  /** Regla con un rombo al centro. Separa sin gritar. */
  const reglaAdornada = (y, x1, x2, color) => {
    const c = color || ORO;
    const cx = (x1 + x2) / 2;
    doc.setDrawColor(...c);
    doc.setLineWidth(0.3);
    doc.line(x1, y, cx - 4, y);
    doc.line(cx + 4, y, x2, y);
    rombo(cx, y, 1.5, c);
  };

  /**
   * El marco de cada página: dos rectángulos y cuatro escuadras en las
   * esquinas. Los adornos que pidió el operador, hechos con líneas y no con
   * una imagen: una imagen de marco pesaría más que las once fotografías
   * juntas y no se podría recolorear para otra vinícola.
   */
  const marco = () => {
    doc.setDrawColor(...FILETE);
    doc.setLineWidth(0.7);
    doc.rect(11, 11, pw - 22, ph - 22);
    doc.setDrawColor(...ORO);
    doc.setLineWidth(0.25);
    doc.rect(14, 14, pw - 28, ph - 28);

    // Escuadras: dos trazos cortos hacia dentro desde cada esquina del marco
    // interior, y un rombo en el vértice.
    const e = 7;
    const esquinas = [
      [14, 14, 1, 1], [pw - 14, 14, -1, 1],
      [14, ph - 14, 1, -1], [pw - 14, ph - 14, -1, -1],
    ];
    doc.setDrawColor(...ORO);
    doc.setLineWidth(0.5);
    for (const [x, y, sx, sy] of esquinas) {
      doc.line(x, y + sy * 2.5, x + sx * e, y + sy * 2.5);
      doc.line(x + sx * 2.5, y, x + sx * 2.5, y + sy * e);
      rombo(x + sx * 2.5, y + sy * 2.5, 0.9, ORO);
    }
  };

  const pie = (n) => {
    fuente("helvetica", "normal", 7.5, [175, 170, 162]);
    const izq = nombreVinicola || "";
    if (izq) doc.text(izq, ml, ph - 17);
    if (n != null) doc.text(String(n), mr, ph - 17, { align: "right" });
  };

  let pagina = 0;
  const nuevaPagina = (conNumero = true) => {
    if (pagina > 0) doc.addPage();
    pagina++;
    marco();
    if (conNumero && pagina > 1) pie(pagina - 1);
    else if (!conNumero) pie(null);
    return yTecho;
  };

  /** Reparte un texto en renglones y devuelve dónde quedó la pluma. */
  const parrafo = (texto, x, ancho, y, tam, color, estilo, familia, interlinea) => {
    fuente(familia || "helvetica", estilo || "normal", tam, color);
    const lineas = doc.splitTextToSize(texto, ancho);
    const lh = interlinea || tam * 0.46;
    let yy = y;
    for (const l of lineas) { doc.text(l, x, yy); yy += lh; }
    return yy;
  };

  /** Cuánto ocuparía ese texto, sin dibujarlo. */
  const altoDe = (texto, ancho, tam, interlinea) => {
    if (!texto) return 0;
    doc.setFontSize(tam);
    return doc.splitTextToSize(texto, ancho).length * (interlinea || tam * 0.46);
  };

  /** Rótulo de sección: versalita coral con su regla debajo. */
  const rotulo = (texto, x, ancho, y) => {
    fuente("helvetica", "bold", 7, CORAL);
    doc.text(espaciar(texto), x, y);
    doc.setDrawColor(...FILETE);
    doc.setLineWidth(0.2);
    doc.line(x, y + 1.6, x + ancho, y + 1.6);
    return y + 5.4;
  };

  // ── Portada ──────────────────────────────────────────────────────────────

  nuevaPagina(false);
  let y = 62;
  try { doc.addImage(LOGO_BASE64, "JPEG", pw / 2 - 17, y, 34, 34); } catch (e) { /* sin logo se sigue */ }
  y += 50;

  if (nombreVinicola) {
    fuente("times", "bold", 30, CORAL);
    doc.text(nombreVinicola.toUpperCase(), pw / 2, y, { align: "center" });
    y += 12;
  }
  reglaAdornada(y, ml + 34, mr - 34);
  y += 14;

  fuente("times", "italic", 17, TINTA);
  doc.text("Cuadernillo de fichas", pw / 2, y, { align: "center" });
  y += 11;

  fuente("helvetica", "normal", 9.5, SUAVE);
  const n = productos.length;
  doc.text(n === 1 ? "1 etiqueta" : `${n} etiquetas`, pw / 2, y, { align: "center" });
  y += 6;
  doc.text(new Date().toLocaleDateString("es-MX",
    { year: "numeric", month: "long", day: "numeric" }), pw / 2, y, { align: "center" });

  // La frase de abajo explica el documento entero en un renglón. Solo aparece
  // si los productos seleccionados de verdad traen obra e ilustración: en una
  // vinícola que no use esos campos, prometería algo que las páginas no dan.
  const hayTrilateral = productos.some(p => p.workTitle || p.artistName);
  if (hayTrilateral) {
    const yl = ph - 62;
    reglaAdornada(yl, pw / 2 - 26, pw / 2 + 26);
    fuente("times", "italic", 11.5, SUAVE);
    doc.text("Cada botella es un vino, una obra y una ilustración.",
      pw / 2, yl + 11, { align: "center" });
  }

  // ── Una página por vino ──────────────────────────────────────────────────

  for (const p of productos) {
    y = nuevaPagina();

    // Cintillo de categoría y grados
    const partes = [];
    const cat = ETIQUETAS_CATEGORIA[p.category];
    if (cat) partes.push(cat);
    if (p.gl > 0) partes.push(`${p.gl}° G.L.`);
    if (partes.length) {
      fuente("helvetica", "normal", 7.5, ORO);
      doc.text(espaciar(partes.join("   ·   ")), pw / 2, y, { align: "center" });
      y += 8;
    }

    // Nombre
    fuente("times", "bold", 23, TINTA);
    for (const l of doc.splitTextToSize(p.name || "", anchoTotal - 20)) {
      doc.text(l, pw / 2, y, { align: "center" });
      y += 9.8;
    }
    y += 1;
    reglaAdornada(y, pw / 2 - 30, pw / 2 + 30);
    y += 10;

    // ── Fila de la fotografía ──────────────────────────────────────────────
    const anchoFoto = 64, altoFoto = 85, separacion = 11;
    const xTexto = ml + anchoFoto + separacion;
    const anchoTexto = mr - xTexto;
    const yFila = y;

    dibujarFoto(doc, p, ml, yFila, anchoFoto, altoFoto);

    let yt = yFila;
    if (p.description) {
      yt = rotulo("La botella", xTexto, anchoTexto, yt);
      yt = parrafo(p.description, xTexto, anchoTexto, yt, 9.5, TINTA, "normal", "helvetica", 4.5) + 4.5;
    }
    if (p.tastingNotes) {
      yt = rotulo("En copa", xTexto, anchoTexto, yt);
      yt = parrafo(p.tastingNotes, xTexto, anchoTexto, yt, 9, SUAVE, "normal", "helvetica", 4.3) + 4.5;
    }
    if (p.pairing) {
      yt = rotulo("A la mesa", xTexto, anchoTexto, yt);
      yt = parrafo(p.pairing, xTexto, anchoTexto, yt, 9.5, TINTA, "italic", "times", 4.5) + 4.5;
    }

    y = Math.max(yFila + altoFoto, yt) + 11;

    // ── La historia detrás de la etiqueta ──────────────────────────────────
    //
    // Va a todo lo ancho y sobre fondo crema porque es el texto que el
    // vendedor cuenta en voz alta. Es lo único de la página que no es dato.
    if (p.labelStory) {
      const alto = altoDe(p.labelStory, anchoTotal - 14, 10, 4.9) + 16;
      if (y + alto > ySuelo) y = nuevaPagina();
      doc.setFillColor(...CREMA);
      doc.rect(ml, y, anchoTotal, alto, "F");
      doc.setDrawColor(...ORO);
      doc.setLineWidth(0.4);
      doc.line(ml, y, ml, y + alto);          // filete vertical a la izquierda

      fuente("helvetica", "bold", 7, CORAL);
      doc.text(espaciar("La historia detrás de la etiqueta"), ml + 7, y + 7);
      parrafo(p.labelStory, ml + 7, anchoTotal - 14, y + 14, 10, TINTA, "italic", "times", 4.9);
      y += alto + 11;
    }

    // ── Las otras dos fichas, lado a lado ──────────────────────────────────
    const obra = componerObra(p);
    const arte = componerArte(p);

    if (obra || arte) {
      const anchoCol = (anchoTotal - 12) / 2;
      const xObra = ml, xArte = ml + anchoCol + 12;

      // Se miden las dos ANTES de dibujar ninguna: si no caben, las dos pasan
      // juntas a la página siguiente. Partirlas dejaría La obra sola al pie y
      // El arte huérfano arriba de la otra hoja.
      const altoObra = obra ? altoDeObra(obra, anchoCol) : 0;
      const altoArte = arte ? altoDeArte(arte, anchoCol) : 0;
      if (y + Math.max(altoObra, altoArte) > ySuelo) y = nuevaPagina();

      if (obra) dibujarObra(obra, xObra, anchoCol, y);
      if (arte) dibujarArte(arte, xArte, anchoCol, y);

      // Filete entre las dos columnas, del alto de la más alta.
      if (obra && arte) {
        doc.setDrawColor(...FILETE);
        doc.setLineWidth(0.2);
        const alto = Math.max(altoObra, altoArte);
        doc.line(ml + anchoCol + 6, y - 2, ml + anchoCol + 6, y + alto - 4);
      }
    }
  }

  // ── Las piezas de las dos fichas de abajo ────────────────────────────────

  function componerObra(p) {
    if (!p.workTitle && !p.workAuthor && !p.workNote) return null;
    const pie = [p.workKind, p.workYear].filter(Boolean).join(", ");
    return { titulo: p.workTitle || "", autor: p.workAuthor || "", pie, nota: p.workNote || "" };
  }

  function componerArte(p) {
    if (!p.artistName && !p.artworkNote && !p.artistNote) return null;
    return { artista: p.artistName || "", obra: p.artworkNote || "", semblanza: p.artistNote || "" };
  }

  function altoDeObra(o, ancho) {
    let h = 5.4;
    if (o.titulo) h += altoDe(o.titulo, ancho, 14, 6);
    if (o.autor) h += altoDe(o.autor, ancho, 10.5, 4.8);
    if (o.pie) h += 4.8;
    h += 3;
    if (o.nota) h += altoDe(o.nota, ancho, 9, 4.2);
    return h;
  }

  function altoDeArte(a, ancho) {
    let h = 5.4;
    if (a.artista) h += altoDe(a.artista, ancho, 12.5, 5.6);
    h += 3;
    if (a.obra) h += altoDe(a.obra, ancho, 9, 4.2) + 3;
    if (a.semblanza) h += altoDe(a.semblanza, ancho, 9, 4.2);
    return h;
  }

  function dibujarObra(o, x, ancho, y) {
    let yy = rotulo("La obra", x, ancho, y);
    if (o.titulo) yy = parrafo(o.titulo, x, ancho, yy, 14, TINTA, "bolditalic", "times", 6);
    if (o.autor) yy = parrafo(o.autor, x, ancho, yy, 10.5, TINTA, "normal", "times", 4.8);
    if (o.pie) {
      fuente("helvetica", "normal", 8, SUAVE);
      doc.text(o.pie, x, yy);
      yy += 4.8;
    }
    yy += 3;
    if (o.nota) parrafo(o.nota, x, ancho, yy, 9, SUAVE, "normal", "helvetica", 4.2);
  }

  function dibujarArte(a, x, ancho, y) {
    let yy = rotulo("El arte", x, ancho, y);
    if (a.artista) yy = parrafo(a.artista, x, ancho, yy, 12.5, TINTA, "bold", "times", 5.6);
    yy += 3;
    if (a.obra) yy = parrafo(a.obra, x, ancho, yy, 9, SUAVE, "normal", "helvetica", 4.2) + 3;
    // La semblanza de la artista casi siempre está vacía y por eso este
    // renglón casi nunca aparece. Cuando la vinícola la escriba, aparece sola.
    if (a.semblanza) parrafo(a.semblanza, x, ancho, yy, 9, SUAVE, "italic", "times", 4.2);
  }

  function dibujarFoto(doc, p, x, y, ancho, alto) {
    doc.setFillColor(255, 255, 255);
    doc.rect(x, y, ancho, alto, "F");

    let dibujada = false;
    if (p.imageBase64) {
      try {
        const formato = p.imageBase64.startsWith("data:image/png") ? "PNG" : "JPEG";
        // Se ENCAJA respetando la proporción, como en la lista de precios: una
        // botella es alta y angosta y estirarla a la caja la deja gorda y
        // chaparra. Aquí importa más todavía, porque es la fotografía grande.
        let ix = x, iy = y, iw = ancho, ih = alto;
        if (typeof doc.getImageProperties === "function") {
          const props = doc.getImageProperties(p.imageBase64);
          if (props && props.width > 0 && props.height > 0) {
            const escala = Math.min(ancho / props.width, alto / props.height);
            iw = props.width * escala;
            ih = props.height * escala;
            ix = x + (ancho - iw) / 2;
            iy = y + (alto - ih) / 2;
          }
        }
        doc.addImage(p.imageBase64, formato, ix, iy, iw, ih);
        dibujada = true;
      } catch (e) { /* una foto ilegible no tumba el cuadernillo */ }
    }

    if (!dibujada) marcaDeAgua(doc, x, y, ancho, alto, p);

    // El marco va sobre el hueco completo y no sobre la foto, para que todas
    // las páginas tengan el mismo rectángulo aunque las fotos no midan igual.
    doc.setDrawColor(...ORO);
    doc.setLineWidth(0.4);
    doc.rect(x, y, ancho, alto);
    doc.setDrawColor(...FILETE);
    doc.setLineWidth(0.2);
    doc.rect(x + 1.6, y + 1.6, ancho - 3.2, alto - 3.2);
  }

  /**
   * El sustituto cuando no hay fotografía. Una botella dibujada con cuatro
   * rectángulos, en crema sobre crema.
   *
   * Podría no dibujarse nada y dejar el hueco en blanco. Se dibuja porque un
   * hueco blanco enmarcado parece un error de impresión, y esto parece lo que
   * es: una ficha a la que le falta su foto.
   */
  function marcaDeAgua(doc, x, y, ancho, alto, p) {
    doc.setFillColor(...CREMA);
    doc.rect(x, y, ancho, alto, "F");

    const cx = x + ancho / 2;
    const base = y + alto - 20;
    const anchoCuerpo = ancho * 0.34;
    const altoCuerpo = alto * 0.42;
    doc.setFillColor(232, 226, 214);
    doc.rect(cx - anchoCuerpo / 2, base - altoCuerpo, anchoCuerpo, altoCuerpo, "F");
    doc.rect(cx - anchoCuerpo * 0.14, base - altoCuerpo - 13, anchoCuerpo * 0.28, 13, "F");
    doc.setFillColor(...ORO);
    doc.rect(cx - anchoCuerpo * 0.17, base - altoCuerpo - 16.5, anchoCuerpo * 0.34, 3.5, "F");
    doc.setFillColor(255, 255, 255);
    doc.rect(cx - anchoCuerpo / 2, base - altoCuerpo * 0.62, anchoCuerpo, altoCuerpo * 0.34, "F");

    fuente("helvetica", "normal", 7, [186, 178, 165]);
    doc.text("sin fotografía", cx, y + alto - 11, { align: "center" });
    const cat = ETIQUETAS_CATEGORIA[p.category];
    if (cat) doc.text(cat, cx, y + alto - 6.5, { align: "center" });
  }

  const limpio = (nombreVinicola || "cuadernillo")
    .replace(/[^a-zA-Z0-9áéíóúñÁÉÍÓÚÑ ]/g, "").replace(/ +/g, "_").substring(0, 30);
  guardarPDF(doc, `Cuadernillo_${limpio}.pdf`);
}

/**
 * Versalita a mano: jsPDF no tiene `letter-spacing`, así que el espaciado de
 * los rótulos se hace metiendo un espacio entre letras. Es feo por dentro y es
 * lo que distingue un rótulo de un renglón de texto por fuera.
 *
 * El separador es U+2009 (ESPACIO FINO) y no un espacio normal: con espacios
 * normales "L A   O B R A" se lee como tres palabras sueltas en vez de una
 * palabra espaciada. Va como `\u2009` escapado A PROPÓSITO — escrito como el
 * carácter literal es invisible en el editor, y el siguiente que venga a tocar
 * esta función lo borraría sin verlo o lo reemplazaría por un espacio normal
 * creyendo que arregla una errata.
 *
 * Se exporta para que las pruebas comprueben los rótulos sin volver a teclear
 * un carácter que no se ve.
 */
export const ESPACIO_FINO = "\u2009";

export function espaciar(texto) {
  return String(texto).toUpperCase().split("").join(ESPACIO_FINO);
}
